import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// TabsList container variants
const tabsListVariants = cva(
  'flex',
  {
    variants: {
      variant: {
        // Default pill-style tabs
        default: 'gap-2 overflow-x-auto',
        // Contained with background (like modal mode toggle)
        contained: 'bg-slate-100 rounded-lg p-1 gap-1',
        // Minimal inline
        inline: 'gap-1',
        // Underlined — border-bottom bar, wraps on small screens
        underlined: 'gap-0 flex-wrap border-b border-slate-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// Individual Tab button variants
const tabVariants = cva(
  'flex items-center gap-2 font-medium transition-all whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'px-4 py-2.5 rounded-xl',
        contained: 'px-2.5 py-1.5 rounded-md text-sm',
        inline: 'px-3 py-2 rounded-lg text-sm',
        underlined: 'px-3 py-2.5 -mb-px border-b-2 border-transparent rounded-none text-sm min-w-0 justify-center',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Default variant states
      {
        variant: 'default',
        active: true,
        className: 'bg-indigo-600 text-white shadow-md',
      },
      {
        variant: 'default',
        active: false,
        className: 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200',
      },
      // Contained variant states
      {
        variant: 'contained',
        active: true,
        className: 'bg-white text-indigo-700 shadow-sm',
      },
      {
        variant: 'contained',
        active: false,
        className: 'text-slate-600 hover:text-slate-900',
      },
      // Inline variant states
      {
        variant: 'inline',
        active: true,
        className: 'bg-slate-200 text-slate-900',
      },
      {
        variant: 'inline',
        active: false,
        className: 'text-slate-600 hover:bg-slate-100',
      },
      // Underlined variant states
      {
        variant: 'underlined',
        active: true,
        className: 'border-b-indigo-600 text-indigo-600',
      },
      {
        variant: 'underlined',
        active: false,
        className: 'text-slate-500 hover:text-slate-700 hover:border-b-slate-300',
      },
    ],
    defaultVariants: {
      variant: 'default',
      active: false,
    },
  }
);

export interface TabsListProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(tabsListVariants({ variant, className }))}
        role="tablist"
        {...props}
      />
    );
  }
);
TabsList.displayName = 'TabsList';

export interface TabProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof tabVariants> {}

const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  ({ className, variant, active, ...props }, ref) => {
    return (
      <button
        ref={ref}
        role="tab"
        aria-selected={active ?? false}
        className={cn(tabVariants({ variant, active, className }))}
        {...props}
      />
    );
  }
);
Tab.displayName = 'Tab';

export { TabsList, Tab, tabsListVariants, tabVariants };
